import React from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const emptyForm = {
  title: "", excerpt: "", content: "", cover_image: "",
  category: "Insights", author: "MFB Editorial", published: true, tags: [],
};

export default function AdminBlog() {
  const [posts, setPosts] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState(null);
  const [show, setShow] = React.useState(false);

  const load = async () => {
    const r = await api.get("/admin/blog");
    setPosts(r.data);
  };
  React.useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyForm); setEditingId(null); setShow(true); };
  const startEdit = (p) => { setForm({ ...p, tags: p.tags || [] }); setEditingId(p.id); setShow(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : form.tags };
      if (editingId) {
        await api.put(`/admin/blog/${editingId}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Post created");
      }
      setShow(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Post deleted");
    load();
  };

  return (
    <div data-testid="admin-blog">
      <Header title="Blog Posts" onAdd={startNew} addLabel="New Post" />

      <div className="mt-8 card-luxury overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {posts.map((p) => (
              <tr key={p.id} data-testid={`blog-row-${p.id}`}>
                <td className="px-5 py-3 text-white">{p.title}</td>
                <td className="px-5 py-3">
                  <Badge className="bg-sky-500/10 text-sky-300 border-sky-500/20 hover:bg-sky-500/15">{p.category}</Badge>
                </td>
                <td className="px-5 py-3">
                  {p.published ? <span className="text-emerald-400 text-xs">Published</span> : <span className="text-slate-500 text-xs">Draft</span>}
                </td>
                <td className="px-5 py-3 text-slate-400 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3 text-right">
                  <IconBtn onClick={() => startEdit(p)} testId={`edit-${p.id}`}><Edit className="w-3.5 h-3.5" /></IconBtn>
                  <IconBtn onClick={() => remove(p.id)} testId={`delete-${p.id}`} variant="danger"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                </td>
              </tr>
            ))}
            {posts.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500 text-sm">No posts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {show && (
        <Modal title={editingId ? "Edit post" : "New post"} onClose={() => setShow(false)}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="blog-form-title" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
              <Field label="Author"><Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></Field>
            </div>
            <Field label="Cover image URL"><Input value={form.cover_image || ""} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></Field>
            <Field label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required /></Field>
            <Field label="Content"><Textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required /></Field>
            <Field label="Tags (comma-separated)">
              <Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            </Field>
            <div className="flex items-center gap-3 text-sm">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
              <span className="text-slate-300">Published</span>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button type="button" onClick={() => setShow(false)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="blog-form-submit">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function Header({ title, onAdd, addLabel }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="overline mb-2">Admin</div>
        <h1 className="font-[Outfit] text-3xl text-white font-light tracking-tight">{title}</h1>
      </div>
      {onAdd && (
        <button onClick={onAdd} className="btn-primary text-xs" data-testid="admin-add-btn">
          <Plus className="w-4 h-4" /> {addLabel}
        </button>
      )}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">{label}</label>
      {React.cloneElement(children, {
        className: `${children.props.className || ""} bg-[#0B1221] border-white/10 text-white focus:border-sky-500`,
      })}
    </div>
  );
}

export function IconBtn({ children, onClick, variant = "default", testId }) {
  const base = "inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 ml-1 transition-colors";
  const cls = variant === "danger" ? "text-rose-400 hover:bg-rose-500/10" : "text-slate-300 hover:bg-white/5";
  return (
    <button onClick={onClick} className={`${base} ${cls}`} data-testid={testId}>
      {children}
    </button>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl glass-strong rounded-2xl border border-white/10 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="font-[Outfit] text-lg text-white">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
